import Axios, { CancelToken } from 'axios';
import { axios, createInstance } from './axios';
import { axiosB2 } from './axiosB2';
import { getB2Payload, parseB2Envelope } from './b2Envelope';
import { handleErrors } from './utils';

/**
 * Environments the current user can view/edit on document folders (B2 REST).
 * Same contract as GetEntornos; filtered by folder permissions.
 * @returns {Promise<Array>}
 */
export function getEntornosForFolders() {
    return axiosB2
        .post('GetEntornosForFolders')
        .then(({ data }) => getB2Payload(parseB2Envelope(data)));
}

let cachedFolderEnvironmentsResult = undefined;
let cachedFolderEnvironmentsPromise = null;

/**
 * Session-scoped deduped fetch for folder environment options.
 * @returns {Promise<Array>}
 */
export function getEntornosForFoldersCached() {
    if (cachedFolderEnvironmentsResult !== undefined) {
        return Promise.resolve(cachedFolderEnvironmentsResult);
    }

    if (!cachedFolderEnvironmentsPromise) {
        cachedFolderEnvironmentsPromise = getEntornosForFolders()
            .then((result) => {
                cachedFolderEnvironmentsResult = result;
                return result;
            })
            .finally(() => {
                cachedFolderEnvironmentsPromise = null;
            });
    }

    return cachedFolderEnvironmentsPromise;
}

export function uploadFile(file, locator, cancelToken, incrementProgressBar) {
    if (!file) return Promise.reject('No file to upload');

    return new Promise((resolve, reject) => {
        const axiosInstance = createInstance({
            transformRequest: [
                (data, headers) => {
                    headers.delete('Authorization');
                    return data;
                },
            ],
            timeout: 0,
        });

        const config = {
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.floor(
                    (progressEvent.loaded * 100) / progressEvent.total,
                );
                incrementProgressBar && incrementProgressBar(percentCompleted);
            },
        };

        axiosInstance
            .put(locator, file, {
                ...config,
                cancelToken: cancelToken.token,
            })
            .then(() => {
                resolve();
            })
            .catch((thrown) => {
                if (Axios.isCancel(thrown)) {
                    console.warn('request cancelled by user');
                } else {
                    handleErrors(reject)(thrown);
                }
            });
    });
}

export function createCancelToken() {
    return CancelToken.source();
}

export function getDocumentToBlob(url, format) {
    return Axios({
        url,
        method: 'GET',
        responseType: 'blob',
    }).then((response) => {
        if (format?.toLowerCase() === 'svg') {
            return new Blob([response.data], {
                type: 'image/svg+xml;charset=utf-8',
            });
        }
        return new Blob([response.data]);
    });
}

export function createDocument({ folderId = '-1', name = '', entity = '', entityId = null }) {
    return new Promise((resolve, reject) => {
        axios
            .post(`documents/v1`, { folderId, name, entity, entityId })
            .then(({ data }) => {
                resolve(data);
            })
            .catch(handleErrors(reject));
    });
}
